import React, { useEffect, useRef } from 'react';
import { UnitSystem } from '../lib/dashboard-types';
import { UNITS, DEFAULT_UNITS, TelemetryLayoutMode } from '../../../../../CommonComponents/telemetry';
import './telemetry-components.css';

interface SettingsPopoverProps {
  open: boolean;
  onClose: () => void;
  precision: number;
  setPrecision: (p: number) => void;
  units: UnitSystem;
  setUnit: (key: string, value: string) => void;
  layoutMode?: TelemetryLayoutMode;
  setLayoutMode?: (mode: TelemetryLayoutMode) => void;
}

export const SettingsPopover: React.FC<SettingsPopoverProps> = ({
  open, onClose, precision, setPrecision, units, setUnit, layoutMode = 'grid', setLayoutMode,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: PointerEvent | MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('mousedown', handlePointerDown, true);
    window.addEventListener('keydown', handleKey, true);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('mousedown', handlePointerDown, true);
      window.removeEventListener('keydown', handleKey, true);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handlePrecisionClick = (p: number) => {
    setPrecision(p);
  };

  const handleUnitClick = (key: string, uk: string) => {
    setUnit(key, uk);
  };

  return (
    <div id="telemetry-settings-popover" ref={ref} data-nodrag className="td-settings-popover">
      <div id="telemetry-settings-header" className="td-settings-header">
        <span id="telemetry-settings-title" className="td-settings-title">
          Dashboard Configuration
        </span>
        <button id="telemetry-settings-close-btn" className="td-settings-close-btn" onClick={onClose}>✕</button>
      </div>
      <div className="td-settings-body">
        {/* Layout Mode */}
        {setLayoutMode && (
          <div id="telemetry-settings-layout-section" className="td-settings-section">
            <div className="td-settings-section-title">
              Layout Mode
            </div>
            <div className="td-settings-seg-wrap">
              {(['grid', 'list'] as const).map((m) => {
                const isActive = layoutMode === m;
                return (
                  <button
                    key={m}
                    id={`telemetry-settings-layout-btn-${m}`}
                    onClick={() => setLayoutMode(m)}
                    className={`td-settings-seg-btn ${isActive ? 'td-settings-seg-btn--active' : ''}`}
                    style={{ textTransform: 'capitalize' }}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Decimal Precision */}
        <div id="telemetry-settings-precision-section" className="td-settings-section">
          <div className="td-settings-section-title">
            Decimal Precision
          </div>
          <div className="td-settings-seg-wrap">
            {[0, 1, 2, 3].map((p) => {
              const isActive = precision === p;
              return (
                <button
                  key={p}
                  id={`telemetry-settings-precision-btn-${p}`}
                  onClick={() => handlePrecisionClick(p)}
                  className={`td-settings-seg-btn ${isActive ? 'td-settings-seg-btn--active' : ''}`}
                >
                  {p}
                </button>
              );
            })}
          </div>
        </div>

        {/* Unit rows */}
        {Object.entries(UNITS).map(([key, group]) => (
          <div key={key} id={`telemetry-settings-unit-section-${key}`} className="td-settings-section" style={{ marginBottom: 14 }}>
            <div className="td-settings-section-title">
              {group.label}
            </div>
            <div className="td-settings-unit-grid">
              {Object.entries(group.units).map(([uk, u]) => {
                const active = ((units as any)[key] ?? (DEFAULT_UNITS as any)[key]) === uk;
                return (
                  <button
                    key={uk}
                    id={`telemetry-settings-unit-btn-${key}-${uk}`}
                    onClick={() => handleUnitClick(key, uk)}
                    className={`td-settings-unit-btn ${active ? 'td-settings-unit-btn--active' : ''}`}
                  >
                    {u.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SettingsPopover;
